import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { CsvContactApiModel } from '../../../api/targeting/model/csv-contact-api.model';
import { CsvImportDialogService, ImportedFile } from '../../../Common/Dialogs/components/CsvImportDialog/csv-import-dialog.service';
import { TargetingListList } from '../models/targeting-list-list.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';


export enum ContactImportableFields {
    email,
    firstName,
    lastName,
    firstLast,
    lastFirst,
    phoneNumber,
    twitterHandle,
    facebookHandle,
    instagramHandle,
    linkedInProfile,
    youtubeHandle,
    blueskyHandle,
    tiktokHandle,
    website,
    bio,
    language,
    notes,
    addressLine1,
    addressLine2,
    postalCode,
    country,
    province,
    city,
    outletName,
    jobTitle
}

export class ContactImportParameters {
    replaceWithSystemContacts: boolean = true;
    reportDestinationEmails: string[] = [];
}

export class ContactImportFileParameters {
    createNewListWithFilename: boolean = true;
    lists: TargetingListList[] = [];
    noListsError?: boolean = false;
}

@Injectable()
export class ContactCsvImportDialogService extends CsvImportDialogService<CsvContactApiModel, ContactImportFileParameters> {

    readonly ALPHA_AND_DASH = /^[A-Za-z\- ]+$/;
    readonly PHONE_NUMBER = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
    readonly CANADA_POSTAL_CODE = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    readonly USA_ZIP_CODE = /(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/;

    // Could probably be fancier
    readonly STREET_ADDRESS = /^([0-9]+)\ [a-zA-Z]+/;

    constructor(
        protected contactsPublicApi: ContactsPublicApi,
        protected localizationService: LocalizationService,
        private messageService: MessageService,
    ) {
        super();
    }

    protected findFieldMappingsFromDataRow(file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>) {
        const row = file.rawData[0];

        for (let i = 0; i < row.length; i++) {
            file.csvColumns.push({
                columnIndex: i,
                columnLabel: this.localizationService.transform('Discover.contactImporter.column') + (i + 1) // 1 based counting for display
            });
        }

        let nameFound = false;
        let bioSet = false;

        for (let i = 0; i < row.length; i++) {
            // Pretty arbitrary, it hopes that the first column is the full name
            // This is at least true for Agility exported CSVs.
            if (i === 0 && row[i].match(this.ALPHA_AND_DASH)) {
                // It's quite unlikely to be a full name without either of these,
                if (row[i].indexOf(' ') >= 0) {
                    this.setTargetColumn(file, ContactImportableFields.firstLast, i);
                    nameFound = true;
                    continue;
                } else if (row[i].indexOf(',') >= 0) {
                    this.setTargetColumn(file, ContactImportableFields.lastFirst, i);
                    nameFound = true;
                    continue;
                }
            }
            if (row[i].indexOf('@') >= 0) {
                this.setTargetColumn(file, ContactImportableFields.email, i);
            } else if (row[i].indexOf('twitter') >= 0) { // @TODO: need to handle X
                this.setTargetColumn(file, ContactImportableFields.twitterHandle, i);
            } else if (row[i].indexOf('facebook') >= 0) {
                this.setTargetColumn(file, ContactImportableFields.facebookHandle, i);
            } else if (row[i].indexOf('linkedin') >= 0) {
                this.setTargetColumn(file, ContactImportableFields.linkedInProfile, i);
            } else if (row[i].indexOf('bluesky') >= 0) {
                this.setTargetColumn(file, ContactImportableFields.blueskyHandle, i);
            } else if (row[i].indexOf('tiktok') >= 0) {
                this.setTargetColumn(file, ContactImportableFields.tiktokHandle, i);
            } else if (row[i].indexOf('youtube') >= 0) {
                this.setTargetColumn(file, ContactImportableFields.youtubeHandle, i);
            } else if (i < row.length - 1 && row[i].match(this.ALPHA_AND_DASH) && row[i + 1].match(this.ALPHA_AND_DASH)) {
                // Assuming name is one of the earlier columns, so dont replace it with possible mismatch later
                // (for example country, then province would also satisfy this condition)
                if (!nameFound) {
                    nameFound = true;
                    this.setTargetColumn(file, ContactImportableFields.firstName, i);
                    this.setTargetColumn(file, ContactImportableFields.lastName, i + 1);
                }
            } else if (row[i].match(this.PHONE_NUMBER)) {
                this.setTargetColumn(file, ContactImportableFields.phoneNumber, i);
            } else if (row[i].match(this.CANADA_POSTAL_CODE) || row[i].match(this.USA_ZIP_CODE)) {
                this.setTargetColumn(file, ContactImportableFields.postalCode, i);
            } else if (row[i].match(this.STREET_ADDRESS)) {
                this.setTargetColumn(file, ContactImportableFields.addressLine1, i);
            // It doesn't get much more arbitrary than this.
            // If it contains | it could be an | separated list of subjects like in
            // agility CSVs, so exclude that.
            } else if (row[i].length > 150 && !row[i].includes('|')) {
                // Could be 2 things, give priority to bio; more likely to be useful
                if (!bioSet) {
                    this.setTargetColumn(file, ContactImportableFields.bio, i);
                    bioSet = true;
                } else {
                    this.setTargetColumn(file, ContactImportableFields.notes, i);
                }
            }
        }
    }

    setFieldDefinitions() {
        this.fieldDefinitions = [
            {
                fieldId : ContactImportableFields.email,
                label : 'Discover.contactImporter.fields.email',
                required: true,
                guessingHelperWords : ['email', 'e-mail', 'e-mail address', 'courriel', 'primary e-mail', 'contact email'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.email = value;
                }
            },
            {
                fieldId : ContactImportableFields.firstName,
                label : 'Discover.contactImporter.fields.firstName',
                required: true,
                guessingHelperWords : ['fname', 'first name', 'first', 'Name, First', 'Given Name', 'prenom', 'prénom', 'contact first name'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.firstName = value;
                }
            },
            {
                fieldId : ContactImportableFields.lastName,
                label : 'Discover.contactImporter.fields.lastName',
                required: true,
                guessingHelperWords : ['lname', 'last name', 'last', 'Name, last', 'surname', 'nom de famille', 'dernier nom', 'surnom', 'contact last name'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.lastName = value;
                }
            },
            {
                fieldId : ContactImportableFields.firstLast,
                label : 'Discover.contactImporter.fields.fullName',
                required: true,
                guessingHelperWords : ['name', 'full name', 'nom', 'contact name'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.firstLast = value;
                }
            },
            {
                fieldId : ContactImportableFields.lastFirst,
                label : 'Discover.contactImporter.fields.fullNameReversed',
                required: true,
                // always choose firstLast by default
                guessingHelperWords : [],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.lastFirst = value;
                }
            },
            {
                fieldId : ContactImportableFields.phoneNumber,
                label : 'Discover.contactImporter.fields.phone',
                guessingHelperWords : [
                    'Phone', 'Number', 'Dial', 'Phone #', 'Contact phone', 'Contact phone number',
                    'Contact phone #', 'téléphone', 'numéro de téléphone'
                ],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.phoneNumber = value;
                }
            },
            {
                fieldId : ContactImportableFields.twitterHandle,
                label : 'Discover.contactImporter.fields.twitter',
                guessingHelperWords : ['Twitter', 'Twitter handle', 'twitter link', 'Contact Twitter Handle', 'X', 'X handle', 'x link', 'Contact X Handle'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.twitterHandle = value;
                }
            },
            {
                fieldId : ContactImportableFields.facebookHandle,
                label : 'Discover.contactImporter.fields.facebook',
                guessingHelperWords : ['facebook', 'facebook profile', 'facebook link', 'fb', 'fb link', 'fb url', 'Contact Social Profile'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.facebookProfile = value;
                }
            },
            {
                fieldId : ContactImportableFields.instagramHandle,
                label : 'Discover.contactImporter.fields.instagram',
                guessingHelperWords : ['instagram', 'insta', 'ig'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.instagramHandle = value;
                }
            },
            {
                fieldId : ContactImportableFields.linkedInProfile,
                label : 'Discover.contactImporter.fields.linkedIn',
                guessingHelperWords : ['LinkedIn'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.linkedInProfile = value;
                }
            },
            {
                fieldId : ContactImportableFields.blueskyHandle,
                label : 'Discover.contactImporter.fields.bluesky',
                guessingHelperWords : ['bluesky', 'Bluesky'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.blueskyHandle = value;
                }
            },
            {
                fieldId : ContactImportableFields.youtubeHandle,
                label : 'Discover.contactImporter.fields.youtube',
                guessingHelperWords : ['YouTube', 'Youtube', 'youtube'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.youTubeHandle = value;
                }
            },
            {
                fieldId : ContactImportableFields.tiktokHandle,
                label : 'Discover.contactImporter.fields.tiktok',
                guessingHelperWords : ['TikTok', 'tiktok'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.tiktokHandle = value;
                }
            },
            {
                fieldId : ContactImportableFields.website,
                label : 'Discover.contactImporter.fields.website',
                guessingHelperWords : ['Website', 'Site', 'Portfolio'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.website = value;
                }
            },
            {
                fieldId : ContactImportableFields.language,
                label : 'Discover.contacImporter.fields.language',
                guessingHelperWords : ['Language', 'Lang', 'Contact working languages'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.language = value;
                }
            },
            {
                fieldId : ContactImportableFields.bio,
                label : 'Discover.contactImporter.fields.editorialProfile',
                guessingHelperWords : ['Description', 'Bio', 'Profile', 'Contact pitching profile'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.bio = value;
                }
            },
            {
                fieldId : ContactImportableFields.addressLine1,
                label : 'Discover.contactImporter.fields.addressLine1',
                guessingHelperWords : ['Address', 'Line 1', 'Street', 'Address Line 1', 'Contact address line 1'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.addressLine1 = value;
                }
            },
            {
                fieldId : ContactImportableFields.addressLine2,
                label : 'Discover.contactImporter.fields.addressLine2',
                guessingHelperWords : ['Line 2', 'Address Line 2', 'Contact address line 2'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.addressLine2 = value;
                }
            },
            {
                fieldId : ContactImportableFields.postalCode,
                label : 'Discover.contactImporter.fields.zip',
                guessingHelperWords : ['Postal', 'Code', 'Zip', 'Contact zip/post code'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.postalCode = value;
                }
            },
            {
                fieldId : ContactImportableFields.country,
                label : 'Discover.contactImporter.fields.country',
                guessingHelperWords : ['Contact Country', 'Country', 'Pays'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.country = value;
                }
            },
            {
                fieldId : ContactImportableFields.province,
                label : 'Discover.contactImporter.fields.state',
                guessingHelperWords : ['Contact Province', 'Province', 'Contact State', 'State', 'Etat'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.province = value;
                }
            },
            {
                fieldId : ContactImportableFields.city,
                label : 'Discover.contactImporter.fields.city',
                guessingHelperWords : ['Contact City', 'City', 'Ville'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.city = value;
                }
            },
            {
                fieldId : ContactImportableFields.outletName,
                label : 'Discover.contactImporter.fields.outletName',
                guessingHelperWords : ['Outlet Name', 'Outlet', 'Media', 'Publication', 'Employer', 'Company', 'Média'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.outletName = value;
                }
            },
            {
                fieldId : ContactImportableFields.jobTitle,
                label : 'Discover.contactImporter.fields.jobTitle',
                guessingHelperWords : ['Job', 'Job Title', 'Title', 'Position', 'Titre'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.jobTitle = value;
                }
            },
            {
                fieldId : ContactImportableFields.notes,
                label : 'Discover.contactImporter.fields.notes',
                guessingHelperWords : ['Notes', 'Comments'],
                addToModel : (model: CsvContactApiModel, value: any) => {
                    model.notes = value;
                }
            }
        ];
    }

    public getValidationErrorsInOneFile(file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>): string[] {
        const errors = [];
        const emailField = file.csvFields.find(x => x.originalField.fieldId === ContactImportableFields.email);
        if (!emailField?.targetColumn) {
            errors.push('Discover.contactImporter.errors.missingEmail');
        }

        const firstNameField = file.csvFields.find(x => x.originalField.fieldId === ContactImportableFields.firstName);
        const lastNameField = file.csvFields.find(x => x.originalField.fieldId === ContactImportableFields.lastName);
        const firstLastField = file.csvFields.find(x => x.originalField.fieldId === ContactImportableFields.firstLast);
        const lastFirstField = file.csvFields.find(x => x.originalField.fieldId === ContactImportableFields.lastFirst);

        const hasName = (!!firstNameField?.targetColumn && !!lastNameField?.targetColumn) || !!firstLastField?.targetColumn || !!lastFirstField?.targetColumn;
        if (!hasName) {
            errors.push('Discover.contactImporter.errors.missingName');
        }

        // if field exists, it must have a value as well
        const hasValue = (!!firstNameField?.targetColumn === !!firstNameField?.exampleValue) &&
                         (!!lastNameField?.targetColumn  === !!lastNameField?.exampleValue)  &&
                         (!!firstLastField?.targetColumn === !!firstLastField?.exampleValue) &&
                         (!!lastFirstField?.targetColumn === !!lastFirstField?.exampleValue) &&
                         (!!emailField?.targetColumn     === !!emailField?.exampleValue);

        if (!hasValue) {
            errors.push('Discover.contactImporter.errors.missingValue');
        }
        // TODO : Disallow selection of conflicting name columns?
        // Backend will just pick one; so it's not the end of the world.
        return errors;
    }

    protected createEmptyOutputModel(file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>): CsvContactApiModel {
        return {
            originalFilename: file.fileName,
            addToListIds: []
        };
    }

    protected findHeaderRow(file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>) {
        file.hasHeaderRow = !file.rawData[0].find(column => column.indexOf('@') > -1);
    }

    importData(parameters: ContactImportParameters): Observable<any> {
        const models = this.constructOutputModels();

        if (models.length === 0) {
            return of(null);
        }

        const listsToCreate = this.importedFiles
            .filter(x => x.fileParameters.createNewListWithFilename)
            .map(x => x.fileName);

        return this.contactsPublicApi.importContacts(
            models,
            parameters.replaceWithSystemContacts,
            parameters.reportDestinationEmails,
            listsToCreate
        ).pipe(
            catchError(error => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.contacImporter.import.error.heading',
                    detail: 'Discover.contacImporter.import.error.message'
                });

                throw error;
            })
        );

    }

    // Override to add in the "add to list" to every object
    protected constructOutputModels(): CsvContactApiModel[] {

        const outputModels: CsvContactApiModel[] = [];
        for (const importedFile of this.importedFiles) {
            const start = importedFile.hasHeaderRow ? 1 : 0;

            for (let i = start; i < importedFile.rawData.length; i++) {
                const model: CsvContactApiModel = this.createEmptyOutputModel(importedFile);
                model.addToListIds = importedFile.fileParameters.lists.map(x => x.id);
                outputModels.push(model);

                importedFile.csvFields.forEach(csvField => {
                    if (csvField.targetColumn) {
                        csvField.originalField.addToModel(model, importedFile.rawData[i][csvField.targetColumn.columnIndex]);
                    }
                });
            }
        }
        return outputModels;
    }

    fileParameterFactory(): ContactImportFileParameters {
        return new ContactImportFileParameters();
    }
}
